import React from 'react';

import {
	AGENT_EDIT_CHROME_DATA,
	AGENT_EDIT_CHROME_DEFAULT_AUTO_DISMISS_AFTER_MS,
	AGENT_EDIT_CHROME_DEFAULT_TELEPOINTER_ENABLED,
} from '@atlaskit/editor-common/collab-agent-edit-chrome';
import type { AgentEditChromeData } from '@atlaskit/editor-common/collab-agent-edit-chrome';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { AIPlugin } from '../../aiPluginType';
import type { StreamingTelepointerIdentity } from '../../types/types';
import { TelepointerAvatar } from '../../ui/components/TelepointerAvatar/TelepointerAvatar';
import { aiSessionContextPluginKey } from '../ai-session-context/ai-session-context-plugin-key';
import { createStreamingTelepointerLabelResolver } from '../createStreamingTelepointerLabelResolver';
import { createTelepointerElement } from '../in-editor-direct-streaming/decorations';
import { removeAgentEditChrome } from './commands';
import { mapAgentEditChromeState } from './mapping';
import { agentEditChromePluginKey } from './plugin-key';
import { withResolvedAgentPresentation } from './presentation';
import { normalizeAgentEditChromeRanges } from './ranges';
import { initialAgentEditChromeState, reduceAgentEditChromeState } from './reducer';
import type { AgentEditChromeAction, AgentEditChromeState } from './types';

const LIST_PARENT_TYPES = new Set(['bulletList', 'orderedList', 'decisionList', 'taskList']);

const hasSameAgentEditOwner = (
	previous: AgentEditChromeState,
	next: AgentEditChromeData,
): boolean =>
	previous.agentId === next.agentId &&
	previous.agentIdentityAccountId === next.agentIdentityAccountId &&
	previous.agentNamedId === next.agentNamedId &&
	previous.agentType === next.agentType &&
	previous.actorUserId === next.actorUserId &&
	previous.invocationId === next.invocationId;

/** Axe `list` requires every list child to be a list item. A telepointer widget on the list itself would sit beside those items, so place it inside the last item. */
function telepointerWidgetPos(doc: PMNode, to: number): number {
	const pos = Math.max(0, Math.min(to, doc.content.size));
	try {
		const $pos = doc.resolve(pos);
		if (LIST_PARENT_TYPES.has($pos.parent.type.name)) {
			return Math.max($pos.start(), pos - 1);
		}
	} catch {
		return pos;
	}
	return pos;
}

export const createAgentEditChromePlugin = ({
	api,
	getIntl,
	portalProviderAPI,
	streamingTelepointerIdentity,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	getIntl: PMPluginFactoryParams['getIntl'];
	portalProviderAPI?: PortalProviderAPI;
	streamingTelepointerIdentity?: StreamingTelepointerIdentity;
}): SafePlugin<AgentEditChromeState> => {
	const getTelepointerLabel = createStreamingTelepointerLabelResolver({
		formatMessage: getIntl().formatMessage,
		streamingTelepointerIdentity,
	});

	return new SafePlugin<AgentEditChromeState>({
		key: agentEditChromePluginKey,
		state: {
			init: () => initialAgentEditChromeState,
			apply: (tr, pluginState) => {
				// AI writes (stream update, restore, commit) replace a span that often
				// covers earlier tool-call nodes. Mapping through them collapses the
				// union onto the node being written — a merged flash, then one node.
				const isAiWrite =
					tr.getMeta('isAIRestoreTransaction') || tr.getMeta('isAIStreamingTransformation');
				const mappedState =
					tr.docChanged && !isAiWrite
						? mapAgentEditChromeState(pluginState, tr.mapping)
						: pluginState;
				const data = tr.getMeta(AGENT_EDIT_CHROME_DATA) as AgentEditChromeData | undefined;
				const hasSameOwner = data && hasSameAgentEditOwner(mappedState, data);
				const previousRanges =
					!isExperimentEnabled('platform_editor_ai_streaming_ux_experience_m1') || hasSameOwner
						? mappedState.ranges
						: [];
				const action: AgentEditChromeAction | undefined = data
					? {
							data: {
								...withResolvedAgentPresentation(data),
								ranges: normalizeAgentEditChromeRanges(tr.doc, [...previousRanges, ...data.ranges]),
							},
							type: 'SHOW',
						}
					: tr.getMeta(agentEditChromePluginKey);

				return action ? reduceAgentEditChromeState(mappedState, action) : mappedState;
			},
		},
		props: {
			decorations: (state) => {
				const pluginState = agentEditChromePluginKey.getState(state);
				const autoDismissAfterMs =
					pluginState?.autoDismissAfterMs ?? AGENT_EDIT_CHROME_DEFAULT_AUTO_DISMISS_AFTER_MS;
				if (!pluginState?.ranges.length || autoDismissAfterMs <= 0) {
					return DecorationSet.empty;
				}

				const highlightStyle = [
					pluginState.highlightBackgroundColor &&
						`--agent-edit-chrome-background-color: ${pluginState.highlightBackgroundColor}`,
					pluginState.highlightBorderColor &&
						`--agent-edit-chrome-border-color: ${pluginState.highlightBorderColor}`,
					pluginState.highlightTextColor && `color: ${pluginState.highlightTextColor}`,
				]
					.filter(Boolean)
					.join('; ');
				const decorations: Decoration[] = pluginState.ranges.map(({ from, to }) =>
					Decoration.inline(from, to, {
						class: 'ai-in-editor-ai-content',
						'data-testid': 'agent-edit-chrome-highlight',
						...(highlightStyle ? { style: highlightStyle } : {}),
					}),
				);
				const activeRange = pluginState.ranges[pluginState.ranges.length - 1];
				if (
					activeRange &&
					(pluginState.telepointerEnabled ?? AGENT_EDIT_CHROME_DEFAULT_TELEPOINTER_ENABLED)
				) {
					const telepointerLabel =
						pluginState.telepointerLabel ?? getTelepointerLabel({ isAgentMentionEnabled: false });
					const { to } = activeRange;
					const { avatarContainer, element, labelElement } =
						createTelepointerElement(telepointerLabel);
					// Keys are deliberately independent of the revision. Producers restamp
					// while content streams in, and a revision-scoped key would make
					// ProseMirror discard the widget and remount the avatar portal on every
					// chunk. The stable key lets the existing telepointer be repositioned
					// instead, so it walks forward without flicker.
					//
					// The redesign always resolves a kind (rovo / agent / agent-third-party /
					// fallback), so its presence — not just an account id — is what switches the
					// widget from the plain label to the shared `TelepointerAvatar` badge. Mirrors
					// `createStreamingTelepointerBadgeUpdater`'s in-editor-direct-streaming treatment
					// so both producers render an identical telepointer.
					if (pluginState.telepointerAgentKind) {
						const portalKey = 'agentEditChromeTelepointerAvatar-0';
						labelElement.style.display = 'none';
						avatarContainer.style.display = '';
						avatarContainer.dataset.agentEditChromeAvatarPortalKey = portalKey;
						// A 2px line the height of the content highlight, in the agent's shared participant colour.
						element.style.setProperty('--agent-edit-chrome-telepointer-line-width', '2px');
						element.style.setProperty('--agent-edit-chrome-telepointer-line-height', '20px');
						if (pluginState.telepointerLineColor) {
							element.style.setProperty(
								'--agent-edit-chrome-telepointer-line-color',
								pluginState.telepointerLineColor,
							);
						}
					}
					decorations.push(
						Decoration.widget(telepointerWidgetPos(state.doc, to), element, {
							key: 'agent-edit-chrome-0',
							side: 0,
						}),
					);
				}

				return DecorationSet.create(state.doc, decorations);
			},
		},
		view: () => {
			let timeout: ReturnType<typeof setTimeout> | undefined;
			let revision = 0;
			const renderedAgentByPortalKey = new Map<
				string,
				{ agentRenderKey: string; container: HTMLElement }
			>();
			const removeStaleAvatarPortals = (activePortalKeys = new Set<string>()) => {
				renderedAgentByPortalKey.forEach((_identity, portalKey) => {
					if (!activePortalKeys.has(portalKey)) {
						portalProviderAPI?.remove(portalKey);
						renderedAgentByPortalKey.delete(portalKey);
					}
				});
			};
			return {
				update: (view) => {
					const pluginState = agentEditChromePluginKey.getState(view.state);
					const { agentId, agentIdentityAccountId, agentName, agentNamedId, telepointerAgentKind } =
						pluginState ?? {};

					// Decorations create the portal containers; synchronize their React avatars after
					// those containers enter the editor DOM.
					const activePortalKeys = new Set<string>();
					if (telepointerAgentKind && portalProviderAPI) {
						// Re-render on a change to any badge input, not just the identity account id —
						// mirrors `createStreamingTelepointerBadgeUpdater`'s render key so both producers
						// stay in sync on identical inputs.
						const agentRenderKey = [
							telepointerAgentKind,
							agentNamedId,
							agentIdentityAccountId,
							agentId,
							agentName,
						].join('|');
						view.dom
							.querySelectorAll<HTMLElement>('[data-agent-edit-chrome-avatar-portal-key]')
							.forEach((container) => {
								const portalKey = container.dataset.agentEditChromeAvatarPortalKey;
								if (!portalKey) {
									return;
								}
								activePortalKeys.add(portalKey);
								const renderedPortal = renderedAgentByPortalKey.get(portalKey);
								if (
									renderedPortal?.agentRenderKey !== agentRenderKey ||
									renderedPortal.container !== container
								) {
									portalProviderAPI.render(
										() =>
											React.createElement(TelepointerAvatar, {
												agentId,
												agentIdentityAccountId,
												agentName,
												agentNamedId,
												kind: telepointerAgentKind,
											}),
										container,
										portalKey,
									);
									renderedAgentByPortalKey.set(portalKey, {
										agentRenderKey,
										container,
									});
								}
							});
					}
					removeStaleAvatarPortals(activePortalKeys);

					// Keep the highlight up across sequential tool calls in the same turn.
					// Auto-dismiss after a single apply would clear it before the next
					// commit restamps later nodes.
					const sessionStatus = aiSessionContextPluginKey.getState(view.state)?.status;
					const holdDismiss = sessionStatus === 'streaming' || sessionStatus === 'complete';
					if (holdDismiss) {
						if (timeout) {
							clearTimeout(timeout);
							timeout = undefined;
						}
						if (pluginState) {
							revision = pluginState.revision;
						}
						return;
					}

					// Session ended: dismiss the current treatment after the configured delay.
					if (!pluginState?.ranges.length) {
						return;
					}
					if (pluginState.revision === revision && timeout) {
						return;
					}
					revision = pluginState.revision;
					if (timeout) {
						clearTimeout(timeout);
					}
					const scheduledRevision = pluginState.revision;
					const autoDismissAfterMs =
						pluginState.autoDismissAfterMs ?? AGENT_EDIT_CHROME_DEFAULT_AUTO_DISMISS_AFTER_MS;
					timeout = setTimeout(
						() => {
							api?.core?.actions.execute(removeAgentEditChrome(scheduledRevision));
						},
						Math.max(0, autoDismissAfterMs),
					);
				},
				destroy: () => {
					if (timeout) {
						clearTimeout(timeout);
					}
					removeStaleAvatarPortals();
				},
			};
		},
	});
};
